'use client';

import {
  SvgDonationFill,
  SvgHomeHeartFill,
  SvgMyFill,
  SvgStore,
} from '@common/icon';
import { cn } from '@core/utils';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import { CustomLink } from '@/app/_components/CustomLink';
import { getFloatingBnbKind } from '@/app/_components/FloatingBnbController/constants';
import {
  DONATION_ROUTES,
  MODULE_ROUTES,
  ROUTES,
  SELECTOR,
  WEL_MARKET_ROUTES,
} from '@/constants';
import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import { useGlobalStore } from '@/stores/GlobalStore';
import { findRoute } from '@/utils';
import { gtmDatasets } from '@/utils/analytics/gtm';

const WELLO_NAVIGATION_ITEMS = [
  {
    name: '홈',
    href: ROUTES.HOME.pathname,
    icon: SvgHomeHeartFill,
    hasNotificationDot: false,
  },
  {
    name: '고향사랑기부',
    href: DONATION_ROUTES.HOME.pathname,
    icon: SvgDonationFill,
    hasNotificationDot: false,
  },
  {
    name: '웰마켙',
    href: WEL_MARKET_ROUTES.HOME.pathname,
    icon: SvgStore,
    hasNotificationDot: false,
  },
  {
    name: '마이페이지',
    href: ROUTES.MY.pathname,
    icon: SvgMyFill,
    hasNotificationDot: false,
  },
];
const NAVIGATION_ANIMATION_DURATION = 300;

export const GlobalNavigation = () => {
  const pathname = usePathname();
  const [animatedHref, setAnimatedHref] = useState<string | null>(null);
  const animationTimeoutRef = useRef<number | null>(null);

  const currentRoute = findRoute(pathname);

  const hideBottomNav = useGlobalStore((state) => state.hideBottomNav);

  const hasBottomNav = (() => {
    if (hideBottomNav) return false;
    if (getFloatingBnbKind(pathname)) return false;

    return currentRoute?.hasBottomNav ?? true;
  })();

  const { request: requestHaptic } = useHandleApp({
    type: MESSAGE_KEYWORD.HAPTIC,
  });

  const animateNavigationItem = (href: string) => {
    if (animationTimeoutRef.current) {
      window.clearTimeout(animationTimeoutRef.current);
    }

    setAnimatedHref(href);

    animationTimeoutRef.current = window.setTimeout(() => {
      setAnimatedHref((currentHref) =>
        currentHref === href ? null : currentHref,
      );
      animationTimeoutRef.current = null;
    }, NAVIGATION_ANIMATION_DURATION);
  };

  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        window.clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  return (
    <nav
      aria-label="메인 내비게이션"
      className={cn(
        'fixed inset-x-0 bottom-0 z-10 hidden w-full justify-center overflow-hidden bg-Gray-white mobile-tablet:flex',
        {
          'pb-[var(--safe-area-bottom,0px)]': hasBottomNav,
        },
      )}
      id={SELECTOR.GLOBAL_NAVIGATION}
    >
      {hasBottomNav && (
        <ul
          className={cn(
            'flex justify-around',
            'shadow-large h-70 w-full items-center border-t-1 border-Gray-50 px-22',
          )}
        >
          {WELLO_NAVIGATION_ITEMS.map(
            ({ name, href, icon, hasNotificationDot }) => {
              const clickFnbDataset = gtmDatasets.clickFnb({
                menu_name: name,
              });

              const isActive =
                href === ROUTES.HOME.pathname
                  ? pathname === ROUTES.HOME.pathname ||
                    pathname === MODULE_ROUTES.KB_HOME.pathname
                  : pathname === href || pathname.startsWith(`${href}/`);

              const isCurrentPage = pathname === href;

              const Icon = icon;

              return (
                <li key={name} className="flex-1">
                  <CustomLink
                    aria-current={isCurrentPage ? 'page' : undefined}
                    {...clickFnbDataset}
                    className={cn(
                      'relative flex-col gap-5 whitespace-nowrap font-500 text-Gray-550 flex-center typo-caption1',
                      {
                        'text-Gray-900 font-700 cursor-default': isActive,
                      },
                    )}
                    href={href}
                    onTouchStart={() => {
                      if (!isCurrentPage) requestHaptic();
                    }}
                    onClick={(e) => {
                      if (isCurrentPage) {
                        e.preventDefault();
                        window.scrollTo({ top: 0, behavior: 'smooth' });

                        return;
                      }

                      animateNavigationItem(href);
                    }}
                  >
                    <div className="relative">
                      <Icon
                        className={cn('size-28 text-Gray-400', {
                          'animate-jump animate-duration-300':
                            animatedHref === href,
                          'text-Gray-black': isActive,
                        })}
                      />
                      {hasNotificationDot ? (
                        <>
                          <span className="sr-only">새 소식 있음</span>
                          <div
                            aria-hidden="true"
                            className="absolute -right-4 -top-2 size-6 rounded-pill bg-R-500"
                          />
                        </>
                      ) : null}
                    </div>
                    {name}
                  </CustomLink>
                </li>
              );
            },
          )}
        </ul>
      )}
    </nav>
  );
};
